import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import TransactionFilters from './components/TransactionFilters';
import TransactionTable from './components/TransactionTable';
import TransactionStats from './components/TransactionStats';
import { merchantsService, transactionsService } from '../../services/supabaseService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const MerchantTransactionOverview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [merchant, setMerchant] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    success: 0,
    pending: 0,
    failed: 0,
    totalAmount: 0,
    successRate: 0
  });

  useEffect(() => {
    if (id) {
      fetchData();
      
      // Subscribe to real-time updates
      const unsubscribe = transactionsService?.subscribe(() => {
        fetchData();
      });

      return () => {
        unsubscribe?.();
      };
    }
  }, [id]);

  useEffect(() => {
    applyFilters();
  }, [transactions, filters, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: merchantData } = await merchantsService?.getById(id);
      const { data: transactionsData } = await transactionsService?.getAll({ merchantId: id });

      if (merchantData) {
        setMerchant(merchantData);
      }

      if (transactionsData) {
        setTransactions(transactionsData);
        calculateStats(transactionsData);
      }
    } catch (error) {
      console.error('Error fetching merchant transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const total = data?.length || 0;
    const success = data?.filter(t => t?.status === 'success')?.length || 0;
    const pending = data?.filter(t => t?.status === 'pending')?.length || 0;
    const failed = data?.filter(t => t?.status === 'failed')?.length || 0;
    const totalAmount = data?.reduce((sum, t) => sum + (parseFloat(t?.amount) || 0), 0);
    const successRate = total > 0 ? (success / total) * 100 : 0;

    setStats({
      total,
      success,
      pending,
      failed,
      totalAmount,
      successRate
    });
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Apply status filter
    if (filters?.status) {
      filtered = filtered?.filter(t => t?.status === filters?.status);
    }

    // Apply payment method filter
    if (filters?.paymentMethod) {
      filtered = filtered?.filter(t => t?.paymentMethod === filters?.paymentMethod);
    }

    // Apply date range filter
    if (filters?.dateFrom) {
      filtered = filtered?.filter(t => new Date(t?.createdAt) >= new Date(filters?.dateFrom));
    }
    if (filters?.dateTo) {
      filtered = filtered?.filter(t => new Date(t?.createdAt) <= new Date(filters?.dateTo));
    }

    // Apply search
    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      filtered = filtered?.filter(t => 
        t?.transactionId?.toLowerCase()?.includes(search) ||
        t?.customerName?.toLowerCase()?.includes(search)
      );
    }

    setFilteredTransactions(filtered);
    calculateStats(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleResetFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Merchant Transactions | PaymentNexus</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                {[...Array(4)]?.map((_, i) => (
                  <div key={i} className="h-24 bg-muted rounded-xl"></div>
                ))}
              </div>
              <div className="h-96 bg-muted rounded-xl"></div>
            </div>
          </main>
        </div>
      </>
    );
  }

  if (!merchant) {
    return (
      <>
        <Helmet>
          <title>Merchant Not Found | PaymentNexus</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={64} className="mx-auto text-muted-foreground mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Merchant Not Found</h2>
              <p className="text-muted-foreground mb-6">The merchant you're looking for doesn't exist</p>
              <Button onClick={() => navigate('/merchants')}>Back to Merchants</Button>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{merchant?.merchantName} - Transactions | PaymentNexus</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/merchants/${id}`)}
                className="flex items-center gap-2"
              >
                <Icon name="ArrowLeft" size={16} />
                Back
              </Button>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">Transactions</h1>
                <p className="text-muted-foreground">{merchant?.merchantName}</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <TransactionStats stats={stats} loading={loading} />

          {/* Filters */}
          <TransactionFilters
            filters={filters}
            searchTerm={searchTerm}
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
            onReset={handleResetFilters}
          />

          {/* Transactions Table */}
          <TransactionTable
            data={filteredTransactions}
            loading={loading}
          />
        </main>
      </div>
    </>
  );
};

export default MerchantTransactionOverview;